import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Search, 
  X, 
  Filter, 
  HelpCircle,
  Sparkles,
  FileText,
  User,
  Hash
} from 'lucide-react';

interface MultiInterviewSearchBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onClear: () => void;
  isSearching: boolean;
  resultsCount: number;
}

export const MultiInterviewSearchBar = ({
  searchQuery,
  onSearchChange,
  onClear,
  isSearching,
  resultsCount
}: MultiInterviewSearchBarProps) => {
  const [showHelp, setShowHelp] = useState(false);

  const searchExamples = [
    { query: "slow onboarding", description: "Find all mentions of text" },
    { query: "type:pain", description: "Search specific note types" },
    { query: "interview:abc123", description: "Search specific interview" },
    { query: "frustrated AND pricing", description: "Multiple keywords" }
  ];

  const addExampleQuery = (query: string) => {
    onSearchChange(query);
    setShowHelp(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search across all interviews... (try 'type:pain' or 'user:Sarah')"
          className="pl-10 pr-24 h-11 border-gray-300 focus:border-blue-500 focus:ring-blue-500/20"
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="h-7 w-7 p-0 hover:bg-gray-100"
            >
              <X className="w-3 h-3" />
            </Button>
          )}
          <Popover open={showHelp} onOpenChange={setShowHelp}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 hover:bg-gray-100"
              >
                <HelpCircle className="w-3 h-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="end">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-sm text-gray-900 mb-2 flex items-center space-x-2">
                    <Sparkles className="w-4 h-4 text-blue-500" />
                    <span>Search Tips</span>
                  </h3>
                  <div className="space-y-2">
                    {searchExamples.map((example, index) => (
                      <div key={index} className="group">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addExampleQuery(example.query)}
                          className="w-full justify-start p-2 h-auto hover:bg-blue-50"
                        >
                          <div className="text-left">
                            <div className="font-mono text-xs text-blue-600 font-medium">
                              {example.query}
                            </div>
                            <div className="text-xs text-gray-600">
                              {example.description}
                            </div>
                          </div>
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="border-t pt-3">
                  <h4 className="font-medium text-xs text-gray-700 mb-2">Advanced Filters:</h4>
                  <div className="space-y-1 text-xs text-gray-600">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-3 h-3" />
                      <span><code>type:</code> - Filter by note type</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Hash className="w-3 h-3" />
                      <span><code>interview:</code> - Specific interview</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <User className="w-3 h-3" />
                      <span><code>user:</code> - Filter by participant</span>
                    </div>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      
      {/* Search Status */}
      {(searchQuery || isSearching) && (
        <div className="flex items-center space-x-2 mt-2">
          {isSearching && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-1" />
              Searching...
            </Badge>
          )}
          {!isSearching && searchQuery && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              {resultsCount} {resultsCount === 1 ? 'result' : 'results'} found
            </Badge>
          )}
          {searchQuery && !isSearching && (
            <Badge variant="outline" className="bg-gray-50 text-gray-600">
              Press Enter or wait for auto-search
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};